'use client';

import { useState } from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Clock,
  DollarSign,
  Star,
  Building2,
  AlertTriangle,
  Trophy,
  Target,
} from 'lucide-react';

import { EmailGateStep } from '@/components/tools/email-gate-step';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

type Step = 1 | 2 | 3 | 4 | 'email' | 'result';

export function SupportBenchmark() {
  const { t, isRTL, locale } = useTranslation();

  // Label for the first-response-time metric. The English label is left
  // exactly as it was; Arabic gets the correct term instead of the
  // unrelated `nav['AI Voice']` string ("المكالمات"), which was showing
  // "Calls" as the name of the response-time metric on /ar.
  const firstResponseTimeLabel =
    locale === 'ar'
      ? 'وقت الرد الأول'
      : t.nav['AI Voice'] || 'First Response Time';

  // Industry benchmarks (based on 2024 research)
  const INDUSTRIES = [
    {
      id: 'ecommerce',
      name: t.tools_pages['E-commerce & Retail'],
      icon: '🛒',
      benchmarks: {
        costPerTicket: 15,
        firstResponseTime: 4, // hours
        resolutionTime: 24, // hours
        csat: 78,
      },
    },
    {
      id: 'saas',
      name: t.tools_pages['SaaS & Technology'],
      icon: '💻',
      benchmarks: {
        costPerTicket: 22,
        firstResponseTime: 2,
        resolutionTime: 18,
        csat: 82,
      },
    },
    {
      id: 'fintech',
      name: t.tools_pages['Fintech & Banking'],
      icon: '🏦',
      benchmarks: {
        costPerTicket: 28,
        firstResponseTime: 1,
        resolutionTime: 12,
        csat: 75,
      },
    },
    {
      id: 'healthcare',
      name: t.industries.Healthcare,
      icon: '🏥',
      benchmarks: {
        costPerTicket: 32,
        firstResponseTime: 2,
        resolutionTime: 24,
        csat: 72,
      },
    },
    {
      id: 'travel',
      name: t.tools_pages['Travel & Hospitality'],
      icon: '✈️',
      benchmarks: {
        costPerTicket: 18,
        firstResponseTime: 1,
        resolutionTime: 8,
        csat: 74,
      },
    },
    {
      id: 'telecom',
      name: t.tools_pages['Telecom & Utilities'],
      icon: '📱',
      benchmarks: {
        costPerTicket: 12,
        firstResponseTime: 3,
        resolutionTime: 48,
        csat: 68,
      },
    },
    {
      id: 'insurance',
      name: t.tools_pages['Insurance'],
      icon: '🛡️',
      benchmarks: {
        costPerTicket: 35,
        firstResponseTime: 4,
        resolutionTime: 72,
        csat: 70,
      },
    },
    {
      id: 'other',
      name: t.demo['Other'],
      icon: '🏢',
      benchmarks: {
        costPerTicket: 20,
        firstResponseTime: 3,
        resolutionTime: 24,
        csat: 75,
      },
    },
  ];

  // Cost per ticket ranges
  const COST_RANGES = [
    { id: 'under-5', label: t.tools_pages['Under $5'], value: 4 },
    { id: '5-10', label: t.tools_pages['$5 - $10'], value: 7.5 },
    { id: '10-15', label: t.tools_pages['$10 - $15'], value: 12.5 },
    { id: '15-25', label: t.tools_pages['$15 - $25'], value: 20 },
    { id: '25-40', label: t.tools_pages['$25 - $40'], value: 32.5 },
    { id: 'over-40', label: t.tools_pages['Over $40'], value: 50 },
    { id: 'unknown', label: t.tools_pages["I don't know"], value: null },
  ];

  // Response time ranges
  const RESPONSE_RANGES = [
    { id: 'under-1h', label: t.tools_pages['Under 1 hour'], value: 0.5 },
    { id: '1-4h', label: t.tools_pages['1 - 4 hours'], value: 2.5 },
    { id: '4-8h', label: t.tools_pages['4 - 8 hours'], value: 6 },
    { id: '8-24h', label: t.tools_pages['8 - 24 hours'], value: 16 },
    { id: 'over-24h', label: t.tools_pages['Over 24 hours'], value: 36 },
  ];

  // CSAT ranges
  const CSAT_RANGES = [
    { id: 'over-90', label: t.tools_pages['90%+'], value: 92 },
    { id: '80-90', label: t.tools_pages['80% - 90%'], value: 85 },
    { id: '70-80', label: t.tools_pages['70% - 80%'], value: 75 },
    { id: '60-70', label: t.tools_pages['60% - 70%'], value: 65 },
    { id: 'under-60', label: t.tools_pages['Under 60%'], value: 55 },
    { id: 'unknown', label: t.tools_pages["I don't track this"], value: null },
  ];

  interface BenchmarkResult {
    metric: string;
    yourValue: number | null;
    benchmark: number;
    percentile: number;
    status: 'excellent' | 'good' | 'average' | 'below' | 'critical' | 'unknown';
    insight: string;
    icon: typeof DollarSign;
    lowerIsBetter?: boolean;
  }

  const [step, setStep] = useState<Step>(1);
  const [industry, setIndustry] = useState<string | null>(null);
  const [costPerTicket, setCostPerTicket] = useState<string | null>(null);
  const [responseTime, setResponseTime] = useState<string | null>(null);
  const [csat, setCsat] = useState<string | null>(null);

  const canProceed = () => {
    switch (step) {
      case 1:
        return industry !== null;
      case 2:
        return costPerTicket !== null;
      case 3:
        return responseTime !== null;
      case 4:
        return csat !== null;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (step === 4) {
      setStep('email');
    } else if (typeof step === 'number') {
      setStep((step + 1) as Step);
    }
  };

  const prevStep = () => {
    if (step === 'result') {
      setStep('email');
    } else if (step === 'email') {
      setStep(4);
    } else if (typeof step === 'number' && step > 1) {
      setStep((step - 1) as Step);
    }
  };

  const calculateBenchmarks = (): BenchmarkResult[] => {
    const selectedIndustry = INDUSTRIES.find((i) => i.id === industry);
    const benchmarks = selectedIndustry?.benchmarks || INDUSTRIES[7].benchmarks;

    const costValue =
      COST_RANGES.find((c) => c.id === costPerTicket)?.value ?? null;
    const responseValue =
      RESPONSE_RANGES.find((r) => r.id === responseTime)?.value ?? null;
    const csatValue = CSAT_RANGES.find((c) => c.id === csat)?.value ?? null;

    const getPercentile = (
      value: number | null,
      benchmark: number,
      lowerIsBetter: boolean,
    ): number => {
      if (value === null) return 50;
      const ratio = value / benchmark;
      if (lowerIsBetter) {
        if (ratio <= 0.5) return 95;
        if (ratio <= 0.75) return 80;
        if (ratio <= 1) return 60;
        if (ratio <= 1.5) return 40;
        if (ratio <= 2) return 20;
        return 10;
      } else {
        if (ratio >= 1.2) return 95;
        if (ratio >= 1.1) return 80;
        if (ratio >= 1) return 60;
        if (ratio >= 0.9) return 40;
        if (ratio >= 0.8) return 20;
        return 10;
      }
    };

    const getStatus = (
      percentile: number,
      isUnknown: boolean,
    ): BenchmarkResult['status'] => {
      if (isUnknown) return 'unknown';
      if (percentile >= 80) return 'excellent';
      if (percentile >= 60) return 'good';
      if (percentile >= 40) return 'average';
      if (percentile >= 20) return 'below';
      return 'critical';
    };

    const costPercentile = getPercentile(
      costValue,
      benchmarks.costPerTicket,
      true,
    );
    const responsePercentile = getPercentile(
      responseValue,
      benchmarks.firstResponseTime,
      true,
    );
    const csatPercentile = getPercentile(csatValue, benchmarks.csat, false);

    return [
      {
        metric: t.tools_pages['Cost per ticket'],
        yourValue: costValue,
        benchmark: benchmarks.costPerTicket,
        percentile: costPercentile,
        status: getStatus(costPercentile, costValue === null),
        lowerIsBetter: true,
        icon: DollarSign,
        insight:
          costValue === null
            ? t.tools_pages[
                'Track this metric to understand your true support costs.'
              ]
            : costValue <= benchmarks.costPerTicket * 0.75
              ? t.tools_pages[
                  "Excellent cost efficiency! You're outperforming most competitors."
                ]
              : costValue <= benchmarks.costPerTicket
                ? t.tools_pages[
                    "Good cost control. There's still room for optimization."
                  ]
                : costValue <= benchmarks.costPerTicket * 1.5
                  ? t.tools_pages[
                      'Your costs are above average. AI automation could help reduce this significantly.'
                    ]
                  : t.tools_pages[
                      'High support costs detected. This is a prime opportunity for AI automation.'
                    ],
      },
      {
        metric: firstResponseTimeLabel,
        yourValue: responseValue,
        benchmark: benchmarks.firstResponseTime,
        percentile: responsePercentile,
        status: getStatus(responsePercentile, responseValue === null),
        lowerIsBetter: true,
        icon: Clock,
        insight:
          responseValue === null
            ? t.tools_pages[
                'Response time directly impacts customer satisfaction.'
              ]
            : responseValue <= benchmarks.firstResponseTime * 0.5
              ? t.tools_pages[
                  'Lightning fast! Your customers appreciate quick responses.'
                ]
              : responseValue <= benchmarks.firstResponseTime
                ? t.tools_pages[
                    "Good response times. You're meeting customer expectations."
                  ]
                : responseValue <= benchmarks.firstResponseTime * 2
                  ? t.tools_pages[
                      'Slower than average. Each hour of delay increases churn risk by 5%.'
                    ]
                  : t.tools_pages[
                      'Critical: Slow responses are likely costing you customers. AI can respond instantly, 24/7.'
                    ],
      },
      {
        metric: t.tools_pages['CSAT Score'],
        yourValue: csatValue,
        benchmark: benchmarks.csat,
        percentile: csatPercentile,
        status: getStatus(csatPercentile, csatValue === null),
        lowerIsBetter: false,
        icon: Star,
        insight:
          csatValue === null
            ? t.tools_pages[
                'Start tracking CSAT to measure customer happiness.'
              ]
            : csatValue >= benchmarks.csat * 1.1
              ? t.tools_pages[
                  'Outstanding! Your customers love your support experience.'
                ]
              : csatValue >= benchmarks.csat
                ? t.tools_pages[
                    "Solid CSAT. You're meeting industry standards."
                  ]
                : csatValue >= benchmarks.csat * 0.9
                  ? t.tools_pages[
                      'Room for improvement. Faster responses and consistent quality can boost this.'
                    ]
                  : t.tools_pages[
                      'Below average CSAT. Consistency and speed are key drivers—AI can help with both.'
                    ],
      },
    ];
  };

  const results = step === 'result' ? calculateBenchmarks() : null;

  const getOverallScore = () => {
    if (!results) return { score: 0, label: '', color: '' };
    const knownResults = results.filter((r) => r.status !== 'unknown');
    if (knownResults.length === 0)
      return {
        score: 50,
        label: t.tools_pages['Incomplete Data'] || 'Incomplete Data',
        color: 'text-neutral-500',
      };

    const avgPercentile =
      knownResults.reduce((sum, r) => sum + r.percentile, 0) /
      knownResults.length;

    if (avgPercentile >= 80)
      return {
        score: avgPercentile,
        label: t.tools_pages['Industry Leader'],
        color: 'text-green-500',
      };
    if (avgPercentile >= 60)
      return {
        score: avgPercentile,
        label: t.tools_pages['Above Average'],
        color: 'text-blue-500',
      };
    if (avgPercentile >= 40)
      return {
        score: avgPercentile,
        label: t.tools_pages['Average'],
        color: 'text-yellow-500',
      };
    if (avgPercentile >= 20)
      return {
        score: avgPercentile,
        label: t.tools_pages['Below Average'],
        color: 'text-orange-500',
      };
    return {
      score: avgPercentile,
      label: t.tools_pages['Needs Improvement'],
      color: 'text-red-500',
    };
  };

  const getStatusColor = (status: BenchmarkResult['status']) => {
    switch (status) {
      case 'excellent':
        return 'bg-green-500';
      case 'good':
        return 'bg-blue-500';
      case 'average':
        return 'bg-yellow-500';
      case 'below':
        return 'bg-orange-500';
      case 'critical':
        return 'bg-red-500';
      case 'unknown':
        return 'bg-neutral-300';
    }
  };

  const getStatusIcon = (status: BenchmarkResult['status']) => {
    switch (status) {
      case 'excellent':
        return <Trophy className="size-5 text-green-500" />;
      case 'good':
        return <TrendingUp className="size-5 text-blue-500" />;
      case 'average':
        return <Target className="size-5 text-yellow-500" />;
      case 'below':
        return <TrendingDown className="size-5 text-orange-500" />;
      case 'critical':
        return <AlertTriangle className="size-5 text-red-500" />;
      case 'unknown':
        return <Target className="size-5 text-neutral-400" />;
    }
  };

  const overallScore = step === 'result' ? getOverallScore() : null;

  return (
    <section className="min-h-screen bg-white pt-32 pb-20 md:pt-40">
      <div className="container max-w-3xl">
        {/* Progress */}
        {step !== 'result' && step !== 'email' && (
          <div className="mb-12">
            <div className="mb-2 flex justify-between text-sm text-neutral-400">
              <span>
                {t.tools_pages['Step']} {step} {t.tools_pages['of 4']}
              </span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-neutral-100">
              <motion.div
                className="h-full bg-black"
                initial={{ width: 0 }}
                animate={{ width: `${((step as number) / 4) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
        )}

        <AnimatePresence mode="wait">
          {/* Step 1: Industry */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-purple-100 px-4 py-2 text-sm font-medium text-purple-700">
                  <TrendingUp className="size-4" />
                  {t.features_pages['Support Cost Benchmarking'] ||
                    'Support Cost Benchmarking'}
                </div>
                <h1 className="mb-2 text-3xl text-black md:text-4xl">
                  {t.tools_pages['What industry are you in?']}
                </h1>
                <p className="text-neutral-500">
                  {
                    t.tools_pages[
                      "We'll compare your metrics against companies in your space."
                    ]
                  }
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {INDUSTRIES.map((ind) => (
                  <button
                    key={ind.id}
                    onClick={() => setIndustry(ind.id)}
                    className={`relative flex h-28 flex-col items-center justify-center gap-2 rounded-2xl border-2 p-3 text-center transition-all ${
                      industry === ind.id
                        ? 'border-black bg-neutral-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    }`}
                  >
                    <div
                      className={`absolute top-3 right-3 flex size-5 items-center justify-center rounded-full transition-all ${
                        industry === ind.id ? 'bg-black' : 'bg-transparent'
                      }`}
                    >
                      <Check
                        className={`size-3 transition-opacity ${industry === ind.id ? 'text-white opacity-100' : 'opacity-0'}`}
                      />
                    </div>
                    <span className="text-3xl">{ind.icon}</span>
                    <span className="text-sm leading-tight font-medium text-black">
                      {ind.name}
                    </span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Cost per Ticket */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h1 className="mb-2 text-3xl text-black md:text-4xl">
                  {t.tools_pages["What's your average cost per ticket?"]}
                </h1>
                <p className="text-neutral-500">
                  {t.tools_pages['Include labor, tools, and overhead costs.']}
                </p>
              </div>

              <div className="grid gap-4">
                {COST_RANGES.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setCostPerTicket(option.id)}
                    className={`flex items-center justify-between rounded-2xl border-2 p-6 transition-all ${
                      costPerTicket === option.id
                        ? 'border-black bg-neutral-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    } ${isRTL ? 'text-right' : 'text-left'}`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`flex size-10 items-center justify-center rounded-xl ${
                          costPerTicket === option.id
                            ? 'bg-black text-white'
                            : 'bg-neutral-100'
                        }`}
                      >
                        <DollarSign className="size-5" />
                      </div>
                      <span className="font-semibold text-black">
                        {option.label}
                      </span>
                    </div>
                    {costPerTicket === option.id && (
                      <Check className="size-6 text-black" />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 3: Response Time */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h1 className="mb-2 text-3xl text-black md:text-4xl">
                  {t.tools_pages["What's your average first response time?"]}
                </h1>
                <p className="text-neutral-500">
                  {
                    t.tools_pages[
                      'How long until a customer gets their first reply?'
                    ]
                  }
                </p>
              </div>

              <div className="grid gap-4">
                {RESPONSE_RANGES.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setResponseTime(option.id)}
                    className={`flex items-center justify-between rounded-2xl border-2 p-6 transition-all ${
                      responseTime === option.id
                        ? 'border-black bg-neutral-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    } ${isRTL ? 'text-right' : 'text-left'}`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`flex size-10 items-center justify-center rounded-xl ${
                          responseTime === option.id
                            ? 'bg-black text-white'
                            : 'bg-neutral-100'
                        }`}
                      >
                        <Clock className="size-5" />
                      </div>
                      <span className="font-semibold text-black">
                        {option.label}
                      </span>
                    </div>
                    {responseTime === option.id && (
                      <Check className="size-6 text-black" />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 4: CSAT */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h1 className="mb-2 text-3xl text-black md:text-4xl">
                  {t.tools_pages["What's your current CSAT score?"]}
                </h1>
                <p className="text-neutral-500">
                  {
                    t.tools_pages[
                      'Customer satisfaction percentage from surveys.'
                    ]
                  }
                </p>
              </div>

              <div className="grid gap-4">
                {CSAT_RANGES.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setCsat(option.id)}
                    className={`flex items-center justify-between rounded-2xl border-2 p-6 transition-all ${
                      csat === option.id
                        ? 'border-black bg-neutral-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    } ${isRTL ? 'text-right' : 'text-left'}`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`flex size-10 items-center justify-center rounded-xl ${
                          csat === option.id
                            ? 'bg-black text-white'
                            : 'bg-neutral-100'
                        }`}
                      >
                        <Star className="size-5" />
                      </div>
                      <span className="font-semibold text-black">
                        {option.label}
                      </span>
                    </div>
                    {csat === option.id && (
                      <Check className="size-6 text-black" />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Email Gate */}
          {step === 'email' && (
            <EmailGateStep
              key="email"
              tool="Support Benchmark"
              onSubmit={() => setStep('result')}
              onBack={prevStep}
            />
          )}

          {/* Result */}
          {step === 'result' && results && overallScore && (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-8"
            >
              <div className="text-center">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-purple-100 px-4 py-2 text-sm font-medium text-purple-700">
                  <Building2 className="size-4" />
                  {INDUSTRIES.find((i) => i.id === industry)?.name}{' '}
                  {t.tools_pages['Benchmark']}
                </div>
                <h1 className="text-4xl text-black md:text-5xl">
                  {t.tools_pages['Your Benchmark Results']}
                </h1>
              </div>

              {/* Overall score */}
              <div className="rounded-3xl bg-black p-10 text-center text-white">
                <div className="mb-2 text-6xl font-bold tracking-tight md:text-7xl">
                  <span className={overallScore.color}>
                    {Math.round(overallScore.score)}
                  </span>
                  <span className="text-3xl text-neutral-500">/100</span>
                </div>
                <div className={`text-2xl font-semibold ${overallScore.color}`}>
                  {overallScore.label}
                </div>
                <div className="mt-4 text-sm text-neutral-400">
                  {t.tools_pages[
                    'Percentile ranking vs. {industry} peers'
                  ].replace(
                    '{industry}',
                    INDUSTRIES.find((i) => i.id === industry)?.name || '',
                  )}
                </div>
              </div>

              {/* Detailed breakdown */}
              <div className="space-y-4">
                <h3 className="text-xl text-black">
                  {t.tools_pages['Metric Breakdown']}
                </h3>
                {results.map((result) => (
                  <div
                    key={result.metric}
                    className="overflow-hidden rounded-2xl border border-neutral-200"
                  >
                    <div className="bg-white p-5">
                      <div className="mb-3 flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(result.status)}
                          <div className={isRTL ? 'text-right' : 'text-left'}>
                            <div className="font-semibold text-black">
                              {result.metric}
                            </div>
                            <div className="text-sm text-neutral-500">
                              {t.tools_pages['Industry avg:']}{' '}
                              {result.metric ===
                              t.tools_pages['Cost per ticket']
                                ? `$${result.benchmark}`
                                : result.metric === firstResponseTimeLabel
                                  ? `${result.benchmark}h`
                                  : `${result.benchmark}%`}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-black">
                            {result.yourValue === null
                              ? '—'
                              : result.metric ===
                                  t.tools_pages['Cost per ticket']
                                ? `$${result.yourValue}`
                                : result.metric === firstResponseTimeLabel
                                  ? `${result.yourValue}h`
                                  : `${result.yourValue}%`}
                          </div>
                          <div
                            className={`text-sm font-medium ${
                              result.status === 'excellent'
                                ? 'text-green-600'
                                : result.status === 'good'
                                  ? 'text-blue-600'
                                  : result.status === 'average'
                                    ? 'text-yellow-600'
                                    : result.status === 'below'
                                      ? 'text-orange-600'
                                      : result.status === 'critical'
                                        ? 'text-red-600'
                                        : 'text-neutral-400'
                            }`}
                          >
                            {result.status === 'unknown'
                              ? t.tools_pages['Not tracked']
                              : t.tools_pages['Top {percentile}%'].replace(
                                  '{percentile}',
                                  (100 - result.percentile).toString(),
                                )}
                          </div>
                        </div>
                      </div>
                      <div className="mb-3 h-2 overflow-hidden rounded-full bg-neutral-100">
                        <motion.div
                          className={`h-full ${getStatusColor(result.status)}`}
                          initial={{ width: 0 }}
                          animate={{ width: `${result.percentile}%` }}
                          transition={{ duration: 0.5, delay: 0.3 }}
                        />
                      </div>
                      <p className="text-sm text-neutral-600">
                        {result.insight}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Improvement potential */}
              {results.some(
                (r) =>
                  r.status === 'below' ||
                  r.status === 'critical' ||
                  r.status === 'average',
              ) && (
                <div className="rounded-2xl border-2 border-dashed border-orange-200 bg-orange-50 p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-orange-100">
                      <Sparkles className="size-5 text-orange-600" />
                    </div>
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <h3 className="text-neutral-900">
                        {t.tools_pages['Room for Improvement Detected']}
                      </h3>
                      <p className="mt-1 text-sm text-neutral-600">
                        {
                          t.tools_pages[
                            'Companies using AI support automation see an average 40% reduction in cost per ticket, 90% faster response times, and 15-point CSAT improvement.'
                          ]
                        }
                      </p>
                      <Link
                        href="/tools/roi-calculator"
                        prefetch={true}
                        className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-orange-600 hover:text-orange-700"
                      >
                        {t.tools_pages['Calculate your potential savings']}
                        <ArrowRight
                          className={`size-4 ${isRTL ? 'rotate-180' : ''}`}
                        />
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {/* Top performer message */}
              {results.every(
                (r) => r.status === 'excellent' || r.status === 'good',
              ) && (
                <div className="rounded-2xl border-2 border-dashed border-green-200 bg-green-50 p-6">
                  <div className="flex items-start gap-4 text-black">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-green-100">
                      <Trophy className="size-5 text-green-600" />
                    </div>
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <h3 className="text-neutral-900">
                        {t.tools_pages["You're a Top Performer!"]}
                      </h3>
                      <p className="mt-1 text-sm text-neutral-600">
                        {
                          t.tools_pages[
                            'Great job! Your support metrics are above average. AI can help you maintain this edge while scaling without adding headcount.'
                          ]
                        }
                      </p>
                      <Link
                        href="/demo"
                        prefetch={true}
                        aria-label={
                          t.common['Learn more about Open and book a demo']
                        }
                        className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-green-600 hover:text-green-700"
                      >
                        {t.tools_pages['See how to stay ahead']}
                        <ArrowRight
                          className={`size-4 ${isRTL ? 'rotate-180' : ''}`}
                        />
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              <Link
                href="/demo"
                prefetch={true}
                aria-label={t.common['Learn more about Open and book a demo']}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-black py-4 text-lg font-semibold text-white transition-all hover:bg-neutral-800"
              >
                {t.tools_pages['Get started now']}
                <ArrowRight className={`size-5 ${isRTL ? 'rotate-180' : ''}`} />
              </Link>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        {step !== 'result' && step !== 'email' && (
          <div className="mt-12 flex items-center justify-between">
            <button
              onClick={prevStep}
              disabled={step === 1}
              className={`flex items-center gap-2 rounded-full px-6 py-3 font-medium transition-all ${
                step === 1
                  ? 'cursor-not-allowed text-neutral-300'
                  : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              <ArrowLeft className={`size-5 ${isRTL ? 'rotate-180' : ''}`} />
              {t.tools_pages['Back']}
            </button>

            <button
              onClick={nextStep}
              disabled={!canProceed()}
              className={`flex items-center gap-2 rounded-full px-8 py-3 font-semibold transition-all ${
                canProceed()
                  ? 'bg-black text-white hover:bg-neutral-800'
                  : 'cursor-not-allowed bg-neutral-200 text-neutral-400'
              }`}
            >
              {step === 4
                ? t.tools_pages['See my results']
                : t.tools_pages['Continue']}
              <ArrowRight className={`size-5 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
          </div>
        )}

        {step === 'result' && (
          <div className="mt-8 text-center">
            <button
              onClick={() => setStep(1)}
              className="text-neutral-500 underline hover:text-neutral-700"
            >
              {t.tools_pages['Start over']}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
